import type { PropsWithChildren } from 'react';
import type React from 'react';

import { ClientRender } from '@change-corgi/core/react/ssr/render';

import { usePsfPromoteOrSharePageData } from 'src/app/pages/psf/promoteOrShare/pageContext';
import { ModalConfirmationProvider, StripeLoader } from 'src/app/shared/components/payments';
import { PaypalProvider } from 'src/app/shared/hooks/payments';

import { AvailablePaymentMethodsProvider } from './availablePaymentMethodsContext';
import type { PromoteFormData } from './formData';
import { PromoteFormDataProvider } from './formData';
import { PromoteFormStateContextProvider } from './formState';
import { useMapPageDataToFormData } from './hooks/useMapPageDataToFormData';
import { useStateInitData } from './hooks/useStateInitData';
import { PromoteFormTrackingContextProvider } from './trackingContext';

const PromoteFormContextProvider: React.FC<PropsWithChildren<PromoteFormData>> = ({ children, ...contextData }) => {
	const stateInitData = useStateInitData(contextData);

	return (
		<PromoteFormDataProvider {...contextData}>
			<PromoteFormStateContextProvider {...stateInitData}>
				<AvailablePaymentMethodsProvider>
					<ModalConfirmationProvider>
						<PromoteFormTrackingContextProvider>{children}</PromoteFormTrackingContextProvider>
					</ModalConfirmationProvider>
				</AvailablePaymentMethodsProvider>
			</PromoteFormStateContextProvider>
		</PromoteFormDataProvider>
	);
};

const ClientPromoteFormProvider: React.FC<PropsWithChildren<EmptyProps>> = ({ children }) => {
	const result = useMapPageDataToFormData();
	if (!result.promoteFormData) return <>{children}</>;
	return (
		<StripeLoader>
			<PaypalProvider>
				<PromoteFormContextProvider {...result.promoteFormData}>{children}</PromoteFormContextProvider>
			</PaypalProvider>
		</StripeLoader>
	);
};

export const PromoteFormProvider: React.FC<PropsWithChildren<EmptyProps>> = ({ children }) => {
	const { data } = usePsfPromoteOrSharePageData();

	if (
		data.promoteShareOnComboExperiment.variation === 'control' ||
		data.promoteShareOnComboExperiment.variation === 'fake_control'
	)
		return <>{children}</>;

	return <ClientRender>{() => <ClientPromoteFormProvider>{children}</ClientPromoteFormProvider>}</ClientRender>;
};
